import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectors } from "../../../store/selectors/shipBuilderSelectors";
import {
  setCrewMode,
  setCrewOverrides,
} from "../../../store/features/shipBuilderSlice";
import {
  CrewRole,
  CREW_SALARIES,
  CrewMode,
  CrewOverrides,
} from "../../../types/crew";
import { useAppSelector } from "../../../store/hooks";
import { selectors as shipBuilderSelectors } from "../../../store/selectors/shipBuilderSelectors";
import { selectIsBreakaway } from "../../../store/selectors/breakawayHullSelectors";
import { formatCurrency } from "../../../utils/formatting";
import { calculateCrewRequirements } from "../../../utils/calculations/crewCalculations";

// Use string keys for all lookups to avoid enum/string mismatch issues

const CrewStep: React.FC = () => {
  const dispatch = useDispatch();
  const crewMode = useSelector(selectors.selectCrewMode);
  const minimumCrewRaw = useSelector(selectors.selectMinimumCrew) as
    | Record<string, number>
    | undefined
    | null;
  const crewOverridesRaw = useSelector(selectors.selectCrewOverrides) as
    | Record<string, number>
    | undefined
    | null;
  const minimumCrew = minimumCrewRaw || {};
  const crewOverrides = crewOverridesRaw || {};
  const crewSalaryTotal = useSelector(selectors.selectCrewSalaryTotal);
  // Get the correct stewards required from the selector
  const stewardsRequired = useAppSelector(
    shipBuilderSelectors.selectStewardsRequired,
  );

  // Breakaway hulls detection and combined crew calculation
  const shipBuilderState = useAppSelector((state) => state.shipBuilder);
  const isBreakaway = useAppSelector(selectIsBreakaway);
  const breakawayHull = useAppSelector((state) => state.breakawayHull);
  const hasBreakawayHulls = isBreakaway || breakawayHull?.isBreakaway;

  const getBreakawayInfo = () => {
    if (!breakawayHull?.sections) return "";
    const sections = breakawayHull.sections;
    return `${sections.length} section${sections.length > 1 ? 's' : ''} (${sections.map(s => `${s.name}: ${s.size} tons`).join(", ")})`;
  };

  // Calculate combined crew requirements for breakaway hulls
  const getCombinedCrewRequirements = () => {
    if (!hasBreakawayHulls || !breakawayHull?.sections) {
      return minimumCrew;
    }

    // For breakaway hulls, crew is assigned to the ship as a whole
    // We need to calculate requirements based on the combined ship configuration
    const combinedShipSize = breakawayHull.sections.reduce((total, section) => total + section.size, 0);
    
    // Create a combined ship state for crew calculation
    const combinedShipState = {
      ...shipBuilderState,
      shipSize: combinedShipSize,
    };

    const crewResult = calculateCrewRequirements(combinedShipState);
    
    if (crewResult.success && crewResult.data) {
      return {
        Captain: crewResult.data.captain,
        Pilot: crewResult.data.pilot,
        Astrogator: crewResult.data.astrogator,
        Engineer: crewResult.data.engineer,
        Maintenance: crewResult.data.maintenance,
        Gunner: crewResult.data.gunner,
        Steward: crewResult.data.steward,
        Administrator: crewResult.data.administrator,
        "Sensor Operator": crewResult.data.sensorOperator,
        Medic: crewResult.data.medic,
        Officer: crewResult.data.officer,
        "Ship's Troops/Marines": crewResult.data.marines,
      };
    }

    return minimumCrew;
  };

  const combinedCrewRequirements = getCombinedCrewRequirements();

  // Handle mode toggle
  const handleModeChange = (mode: CrewMode) => {
    dispatch(setCrewMode(mode));
  };

  // Handle override change, ensuring no negative numbers
  const handleOverrideChange = (role: string, value: number) => {
    const safeValue = Math.max(0, value); // No negative crew
    const newOverrides: CrewOverrides = { ...crewOverrides, [role]: safeValue };
    dispatch(setCrewOverrides(newOverrides));
  };

  return (
    <div className="pb-32">
      <h2 className="text-2xl font-bold mb-4 text-highlightOrange">
        Crew Requirements
      </h2>

      {/* Breakaway Hulls Detection */}
      {hasBreakawayHulls && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
                Breakaway Hulls Detected
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                {getBreakawayInfo()}
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Note:</strong> Crew is assigned to the ship as a whole, not tracked per section. 
                Crew requirements are calculated based on the combined ship configuration.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mode Toggle */}
      <div
        className="flex items-center mb-6"
        role="radiogroup"
        aria-label="Crew Mode"
      >
        <span className="mr-4 font-semibold">Mode:</span>
        <button
          className={`px-4 py-2 rounded-l bg-gray-800 text-white font-bold ${crewMode === "Commercial" ? "bg-highlightOrange text-black" : ""}`}
          onClick={() => handleModeChange("Commercial")}
          aria-pressed={crewMode === "Commercial"}
          aria-label="Commercial Mode"
        >
          Commercial
        </button>
        <button
          className={`px-4 py-2 rounded-r bg-gray-800 text-white font-bold border-l border-gray-700 ${crewMode === "Military" ? "bg-highlightOrange text-black" : ""}`}
          onClick={() => handleModeChange("Military")}
          aria-pressed={crewMode === "Military"}
          aria-label="Military Mode"
        >
          Military
        </button>
      </div>
      {/* Crew Table */}
      <div className="overflow-x-auto">
        <table
          className="min-w-full bg-white dark:bg-gray-800 rounded shadow"
          aria-label="Crew Requirements Table"
        >
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700">
              <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Position</th>
              <th className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">Recommended Minimum</th>
              <th className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">Assigned</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(CREW_SALARIES).map((role) => {
              const salary = CREW_SALARIES[role as CrewRole];
              const min =
                role === "Steward"
                  ? stewardsRequired.total
                  : (hasBreakawayHulls ? combinedCrewRequirements[role] : minimumCrew[role]) ?? 0;
              const assigned =
                crewOverrides[role] !== undefined ? crewOverrides[role] : min;
              if (!salary) {
                return (
                  <tr key={role} className="bg-red-100">
                    <td colSpan={3} className="text-red-500">
                      Error: Missing salary for {role}
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={role} className="border-b border-gray-200 dark:border-gray-600 last:border-b-0">
                  <td className="px-4 py-2 font-semibold text-gray-900 dark:text-gray-100">{role}</td>
                  <td className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">{min}</td>
                  <td className="px-4 py-2 text-right">
                    <input
                      type="number"
                      min={0}
                      aria-label={`Assigned crew for ${role}`}
                      className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-right focus:outline-none focus:ring-2 focus:ring-highlightOrange dark:bg-gray-700 dark:text-gray-100"
                      value={assigned}
                      onChange={(e) =>
                        handleOverrideChange(
                          role,
                          Math.max(Number(e.target.value), 0),
                        )
                      }
                    />
                  </td>
                </tr>
              );
            })}
            {/* Ship's Troops/Marines row */}
            <tr className="border-b border-gray-200 dark:border-gray-600 last:border-b-0">
              <td className="px-4 py-2 font-semibold text-gray-900 dark:text-gray-100">Ship's Troops/Marines</td>
              <td className="px-4 py-2 text-right text-gray-900 dark:text-gray-100">—</td>
              <td className="px-4 py-2 text-right">
                <input
                  type="number"
                  min={0}
                  aria-label="Assigned crew for Ship's Troops/Marines"
                  className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-right focus:outline-none focus:ring-2 focus:ring-highlightOrange dark:bg-gray-700 dark:text-gray-100"
                  value={crewOverrides["Ship's Troops/Marines"] ?? 0}
                  onChange={(e) =>
                    handleOverrideChange(
                      "Ship's Troops/Marines",
                      Math.max(Number(e.target.value), 0),
                    )
                  }
                />
              </td>
            </tr>
          </tbody>
        </table>
        {/* Reset to Recommended Button */}
        <div className="flex justify-end mt-4">
          <button
            className="bg-highlightOrange hover:bg-orange-500 text-black font-bold py-2 px-6 rounded shadow focus:outline-none focus:ring-2 focus:ring-highlightOrange"
            onClick={() => {
              // Reset all crewOverrides to current minimumCrew or combinedCrewRequirements
              const resetOverrides: CrewOverrides = {};
              const requirementsToUse = hasBreakawayHulls ? combinedCrewRequirements : minimumCrew;
              Object.keys(requirementsToUse).forEach((role) => {
                resetOverrides[role as keyof CrewOverrides] =
                  requirementsToUse[role] ?? 0;
              });
              dispatch(setCrewOverrides(resetOverrides));
            }}
            aria-label="Reset all assigned crew to recommended minimums"
          >
            Reset to Recommended
          </button>
        </div>
      </div>
      {/* Sticky Footer */}
      {!hasBreakawayHulls && (
        <div
          className="fixed bottom-0 left-0 right-0 z-20 bg-gray-900 text-white shadow-lg py-4 px-8 flex items-center justify-between"
          role="contentinfo"
          aria-label="Total Crew Salary"
        >
          <div className="text-lg font-semibold">Total Crew Salary</div>
          <div className="text-2xl font-bold text-green-400">
            {formatCurrency(crewSalaryTotal)}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewStep;
